import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './BugReport.css';

// Bug Report API service
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/';

const bugReportApi = axios.create({
  baseURL: `${API_BASE_URL}api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

const handleApiError = (error) => {
  if (error.response) {
    throw new Error(error.response.data.detail || 'An error occurred');
  } else if (error.request) {
    throw new Error('Network error - please check your connection');
  } else {
    throw new Error('An unexpected error occurred');
  }
};

const bugReportService = {
  submitBugReport: async (bugData) => {
    try {
      const response = await bugReportApi.post('/bug-reports/submit', bugData);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  }
};

const BugReport = () => {
  const [formData, setFormData] = useState({
    user_email: '',
    page_entity: '',
    page_entity_details: '',
    severity: 2, // Default to Important
    details: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  // Get user email from localStorage if available
  useEffect(() => {
    const userEmail = localStorage.getItem('userEmail');
    if (userEmail) {
      setFormData(prev => ({ ...prev, user_email: userEmail }));
    }

    // Set page entity and details from URL parameters if available
    const urlParams = new URLSearchParams(window.location.search);
    const page = urlParams.get('page') || window.location.pathname.split('/')[1] || 'general';
    const entityDetails = urlParams.get('entity') || '';
    setFormData(prev => ({ 
      ...prev, 
      page_entity: page,
      page_entity_details: entityDetails
    }));
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'severity' ? parseInt(value) : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.user_email || !formData.page_entity || !formData.details) {
      setError('Please fill in all required fields');
      return;
    }

    if (formData.details.length < 10) {
      setError('Please provide more detailed information about the bug (at least 10 characters)');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await bugReportService.submitBugReport(formData);
      setSubmitted(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const severityOptions = [
    { value: 3, label: 'Major', emoji: '🚨', description: 'Critical bug that breaks functionality' },
    { value: 2, label: 'Important', emoji: '⚠️', description: 'Significant issue affecting user experience' },
    { value: 1, label: 'Minor / Improvement', emoji: '💡', description: 'Small bug or enhancement suggestion' }
  ];

  if (submitted) {
    return (
      <div className="bug-report-container">
        <div className="bug-report-card">
          <div className="bug-report-success">
            <div className="success-icon">✅</div>
            <h2>Bug Report Submitted!</h2>
            <p>Thank you for helping us improve PrepLetter. We'll review your report and get back to you if needed.</p>
            <button 
              className="btn-primary"
              onClick={() => window.history.back()}
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bug-report-container">
      <div className="bug-report-card">
        <div className="bug-report-header">
          <div className="bug-report-icon">🐛</div>
          <h1>Report a Bug</h1>
          <p>Help us improve PrepLetter by reporting bugs or suggesting improvements</p>
        </div>

        <form onSubmit={handleSubmit} className="bug-report-form">
          {error && (
            <div className="error-message">
              <span className="error-icon">❌</span>
              {error}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="user_email">
              Email Address <span className="required">*</span>
            </label>
            <input
              type="email"
              id="user_email"
              name="user_email"
              value={formData.user_email}
              onChange={handleChange}
              placeholder="your.email@example.com"
              required
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label htmlFor="page_entity">
              Page or Feature <span className="required">*</span>
            </label>
            <input
              type="text"
              id="page_entity"
              name="page_entity"
              value={formData.page_entity}
              onChange={handleChange}
              placeholder="e.g., quiz, prepletters, dashboard"
              required
              className="form-input"
            />
            <small className="form-hint">Which page or feature did you encounter the bug on?</small>
          </div>

          <div className="form-group">
            <label htmlFor="page_entity_details">
              Specific Details (Optional)
            </label>
            <input
              type="text"
              id="page_entity_details"
              name="page_entity_details"
              value={formData.page_entity_details}
              onChange={handleChange}
              placeholder="e.g., quiz-123, prepletter-5-python"
              className="form-input"
            />
            <small className="form-hint">Additional context like quiz ID, letter number, etc.</small>
          </div>

          <div className="form-group">
            <label>
              Bug Severity <span className="required">*</span>
            </label>
            <div className="severity-options">
              {severityOptions.map((option) => (
                <div
                  key={option.value}
                  className={`severity-option ${formData.severity === option.value ? 'selected' : ''}`}
                  onClick={() => setFormData(prev => ({ ...prev, severity: option.value }))}
                >
                  <input
                    type="radio"
                    name="severity"
                    value={option.value}
                    checked={formData.severity === option.value}
                    onChange={handleChange}
                    className="severity-radio"
                  />
                  <div className="severity-content">
                    <div className="severity-header">
                      <span className="severity-emoji">{option.emoji}</span>
                      <span className="severity-label">{option.label}</span>
                    </div>
                    <span className="severity-description">{option.description}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="details">
              Bug Details <span className="required">*</span>
            </label>
            <textarea
              id="details"
              name="details"
              value={formData.details}
              onChange={handleChange}
              placeholder="Please describe the bug in detail. Include steps to reproduce, expected vs actual behavior, and any error messages you saw."
              required
              className="form-textarea"
              rows={6}
              minLength={10}
            />
            <small className="form-hint">
              {formData.details.length}/10 characters minimum. Be as specific as possible to help us fix the issue.
            </small>
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => window.history.back()}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="loading-spinner"></span>
                  Submitting...
                </>
              ) : (
                'Submit Bug Report'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BugReport;
